import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faPlus, faPercentage, faCalendarAlt, faMoneyBillWave, faTag } from '@fortawesome/free-solid-svg-icons';

const AddLoanProduct = ({ isOpen, onClose, onAdd, isLoading }) => {
  const [formData, setFormData] = useState({
    loanProductName: '',
    interestRate: '',
    defaultTermMonths: '',
    lateFeeFixedAmount: '',
  });

  const [errors, setErrors] = useState({});

  const formatNumber = (value) => {
    if (!value) return '';
    const numericValue = value.replace(/,/g, '');
    if (!/^\d*\.?\d*$/.test(numericValue)) return value;
    return Number(numericValue).toLocaleString('en-IN');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    let processedValue = value;
    if (name === 'lateFeeFixedAmount') {
      processedValue = formatNumber(value);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: processedValue,
    }));
    
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.loanProductName.trim()) {
      newErrors.loanProductName = 'Product Name is required';
    } else {
      const productNameRegex = /^.*? - \d+ months$/;
      if (!productNameRegex.test(formData.loanProductName.trim())) {
        newErrors.loanProductName = 'Product Name must be in "Product Name - {no} months" format';
      }
    }

    if (!formData.interestRate || parseFloat(formData.interestRate) < 0) {
      newErrors.interestRate = 'Must be a positive decimal';
    }

    if (!formData.defaultTermMonths || !Number.isInteger(parseFloat(formData.defaultTermMonths)) || parseFloat(formData.defaultTermMonths) <= 0) {
      newErrors.defaultTermMonths = 'Must be a positive integer';
    }

    if (!formData.lateFeeFixedAmount || parseFloat(formData.lateFeeFixedAmount.replace(/,/g, '')) < 0) {
      newErrors.lateFeeFixedAmount = 'Must be a positive decimal';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const payload = {
      loanProductName: formData.loanProductName.trim(),
      interestRate: parseFloat(formData.interestRate),
      defaultTermMonths: parseInt(formData.defaultTermMonths),
      lateFeeFixedAmount: parseFloat(formData.lateFeeFixedAmount.replace(/,/g, '')),
    };

    try {
      const response = await onAdd(payload);
      
      if (response && response.status === -1) {
        setErrors({
          loanProductName: 'Loan product already exists in the database',
        });
        return;
      }

      setFormData({
        loanProductName: '',
        interestRate: '',
        defaultTermMonths: '',
        lateFeeFixedAmount: '',
      });
      setErrors({});
      onClose();
    } catch (err) {
      console.error('Error adding loan product:', err);
      if (err.response && err.response.data && err.response.data.status === -1) {
        setErrors({
          loanProductName: 'Loan product already exists in the database',
        });
      } else {
        setErrors({
          general: 'Failed to add loan product. Please try again.',
        });
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-900/70 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fadeIn">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md transform transition-all duration-300 scale-100">
        {/* Modal Header */}
        <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
              <FontAwesomeIcon icon={faPlus} className="text-white text-sm" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">New Loan Product</h2>
              <p className="text-sm text-gray-500">Add details to create a new loan product</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
            aria-label="Close modal"
          >
            <FontAwesomeIcon icon={faTimes} className="text-gray-400 hover:text-gray-600" />
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Form Fields Container */}
          <div className="space-y-5">
            {/* Loan Product Name */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={faTag} className="text-blue-500 text-sm" />
                <label className="text-sm font-medium text-gray-700">
                  Product Name
                </label>
              </div>
              <div className="relative">
                <input
                  type="text"
                  name="loanProductName"
                  value={formData.loanProductName}
                  onChange={handleChange}
                  placeholder="e.g., Personal Loan - 12 months"
                  className={`w-full px-4 py-3 rounded-xl border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 ${
                    errors.loanProductName
                      ? 'border-red-300 focus:ring-red-500 bg-red-50'
                      : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-400'
                  }`}
                />
                {errors.loanProductName && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  </div>
                )}
              </div>
              {errors.loanProductName && (
                <p className="text-red-500 text-xs font-medium flex items-center gap-1">
                  <span className="w-1 h-1 bg-red-500 rounded-full"></span>
                  {errors.loanProductName}
                </p>
              )}
            </div>

            {/* Interest Rate */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <FontAwesomeIcon icon={faPercentage} className="text-emerald-500 text-sm" />
                  <label className="text-sm font-medium text-gray-700">
                    Interest Rate
                  </label>
                </div>
                <div className="relative">
                  <input
                    type="number"
                    name="interestRate"
                    value={formData.interestRate}
                    onChange={handleChange}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    className={`w-full px-4 py-3 rounded-xl border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 ${
                      errors.interestRate
                        ? 'border-red-300 focus:ring-red-500 bg-red-50'
                        : 'border-gray-300 focus:ring-emerald-500 focus:border-emerald-500 hover:border-gray-400'
                    }`}
                  />
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                    %
                  </div>
                </div>
                {errors.interestRate && (
                  <p className="text-red-500 text-xs font-medium">{errors.interestRate}</p>
                )}
              </div>

              {/* Default Term Months */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <FontAwesomeIcon icon={faCalendarAlt} className="text-purple-500 text-sm" />
                  <label className="text-sm font-medium text-gray-700">
                    Term Length
                  </label>
                </div>
                <div className="relative">
                  <input
                    type="number"
                    name="defaultTermMonths"
                    value={formData.defaultTermMonths}
                    onChange={handleChange}
                    placeholder="12"
                    step="1"
                    min="1"
                    className={`w-full px-4 py-3 rounded-xl border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 ${
                      errors.defaultTermMonths
                        ? 'border-red-300 focus:ring-red-500 bg-red-50'
                        : 'border-gray-300 focus:ring-purple-500 focus:border-purple-500 hover:border-gray-400'
                    }`}
                  />
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                    months
                  </div>
                </div>
                {errors.defaultTermMonths && (
                  <p className="text-red-500 text-xs font-medium">{errors.defaultTermMonths}</p>
                )}
              </div>
            </div>

            {/* Late Fee Fixed Amount */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={faMoneyBillWave} className="text-amber-500 text-sm" />
                <label className="text-sm font-medium text-gray-700">
                  Late Fee Amount
                </label>
              </div>
              <div className="relative ">
                <input
                  type="text"
                  name="lateFeeFixedAmount"
                  value={formData.lateFeeFixedAmount}
                  onChange={handleChange}
                  placeholder="0.00"
                  className={`w-full px-8 py-3  rounded-xl border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 ${
                    errors.lateFeeFixedAmount
                      ? 'border-red-300 focus:ring-red-500 bg-red-50'
                      : 'border-gray-300 focus:ring-amber-500 focus:border-amber-500 hover:border-gray-400'
                  }`}
                />
                <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">
                  Rs
                </div>
              </div>
              {errors.lateFeeFixedAmount && (
                <p className="text-red-500 text-xs  font-medium">  {errors.lateFeeFixedAmount}</p>
              )}
            </div>
          </div>

          {errors.general && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-red-600 text-sm font-medium">{errors.general}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 px-4 py-3 rounded-xl border border-gray-300 bg-white text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 hover:shadow-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium hover:from-blue-700 hover:to-indigo-700 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl hover:shadow-blue-500/20 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Creating...</span>
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faPlus} className="text-sm" />
                  <span>Create Product</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddLoanProduct;